import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import SignatureCanvas from 'react-signature-canvas';

const WaiverSettingsModal = ({ isOpen, onClose, onSave, initialData = null }) => {
  const [activeTab, setActiveTab] = useState('pm_home');
  
  const [pmHomeSettings, setPmHomeSettings] = useState({
    empresa: 'pm_home',
    contratante_nome: 'PM Home Remodeling',
    contratante_endereco: '',
    contratante_cidade_estado_cep: '',
    contratante_telefone: '',
    contratante_email: '',
    contratante_ein: '',
    contratante_nome_cargo: '',
    contratante_assinatura: ''
  });
  
  const [bimcleSettings, setBimcleSettings] = useState({
    empresa: 'bimcle',
    contratante_nome: 'Bimcle Construction',
    contratante_endereco: '',
    contratante_cidade_estado_cep: '',
    contratante_telefone: '',
    contratante_email: '',
    contratante_ein: '',
    contratante_nome_cargo: '',
    contratante_assinatura: ''
  });
  
  const pmHomeSignRef = useRef();
  const bimcleSignRef = useRef();
  
  // Salvar assinatura ao desenhar
  const handleSignatureEnd = (empresa) => {
    if (empresa === 'pm_home' && pmHomeSignRef.current && !pmHomeSignRef.current.isEmpty()) {
      const signature = pmHomeSignRef.current.toDataURL();
      setPmHomeSettings(prev => ({ ...prev, contratante_assinatura: signature }));
    } else if (empresa === 'bimcle' && bimcleSignRef.current && !bimcleSignRef.current.isEmpty()) {
      const signature = bimcleSignRef.current.toDataURL();
      setBimcleSettings(prev => ({ ...prev, contratante_assinatura: signature }));
    }
  };
  
  useEffect(() => {
    if (initialData) {
      if (initialData.pm_home) {
        setPmHomeSettings(initialData.pm_home);
      }
      if (initialData.bimcle) {
        setBimcleSettings(initialData.bimcle);
      }
    }
  }, [initialData]);
  
  // Recarregar assinaturas quando trocar de tab
  useEffect(() => {
    // Pequeno delay para evitar race condition
    const timer = setTimeout(() => {
      if (activeTab === 'pm_home' && pmHomeSettings.contratante_assinatura && pmHomeSignRef.current) {
        try {
          if (!pmHomeSignRef.current.isEmpty()) {
            pmHomeSignRef.current.clear();
          }
          pmHomeSignRef.current.fromDataURL(pmHomeSettings.contratante_assinatura);
        } catch (e) {
          console.error('Erro ao carregar assinatura PM Home:', e);
        }
      } else if (activeTab === 'bimcle' && bimcleSettings.contratante_assinatura && bimcleSignRef.current) {
        try {
          if (!bimcleSignRef.current.isEmpty()) {
            bimcleSignRef.current.clear();
          }
          bimcleSignRef.current.fromDataURL(bimcleSettings.contratante_assinatura);
        } catch (e) {
          console.error('Erro ao carregar assinatura Bimcle:', e);
        }
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [activeTab]);
  
  const handleChange = (empresa, e) => {
    const { name, value } = e.target;
    if (empresa === 'pm_home') {
      setPmHomeSettings(prev => ({ ...prev, [name]: value }));
    } else {
      setBimcleSettings(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleSubmit = async () => {
    // Validar PM Home
    if (!pmHomeSettings.contratante_endereco || !pmHomeSettings.contratante_email) {
      alert('Preencha todos os campos obrigatórios de PM Home Remodeling');
      return;
    }
    
    // Validar assinatura pelo estado, não pelo canvas
    if (!pmHomeSettings.contratante_assinatura) {
      alert('Assinatura de PM Home Remodeling é obrigatória');
      return;
    }
    
    // Validar Bimcle
    if (!bimcleSettings.contratante_endereco || !bimcleSettings.contratante_email) {
      alert('Preencha todos os campos obrigatórios de Bimcle Construction');
      return;
    }
    
    // Validar assinatura pelo estado, não pelo canvas
    if (!bimcleSettings.contratante_assinatura) {
      alert('Assinatura de Bimcle Construction é obrigatória');
      return;
    }
    
    // Usar assinaturas do estado
    const pmHomePayload = pmHomeSettings;
    const bimclePayload = bimcleSettings;
    
    await onSave(pmHomePayload, bimclePayload);
  };
  
  const renderForm = (settings, onChange, signRef, empresa) => (
    <>
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Endereço Completo *</Label>
              <Input 
                name="contratante_endereco" 
                value={settings.contratante_endereco} 
                onChange={(e) => onChange(empresa, e)}
                placeholder="123 Main Street, Suite 100"
              />
            </div>
            <div>
              <Label>Cidade/Estado/CEP *</Label>
              <Input 
                name="contratante_cidade_estado_cep" 
                value={settings.contratante_cidade_estado_cep} 
                onChange={(e) => onChange(empresa, e)}
                placeholder="Atlanta, GA 30303"
              />
            </div>
            <div>
              <Label>Telefone *</Label>
              <Input 
                name="contratante_telefone" 
                value={settings.contratante_telefone} 
                onChange={(e) => onChange(empresa, e)}
                placeholder="(404) 555-0123"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input 
                name="contratante_email" 
                type="email"
                value={settings.contratante_email} 
                onChange={(e) => onChange(empresa, e)}
                placeholder="contact@company.com"
              />
            </div>
            <div>
              <Label>EIN (Federal Tax ID) *</Label>
              <Input 
                name="contratante_ein" 
                value={settings.contratante_ein} 
                onChange={(e) => onChange(empresa, e)}
                placeholder="XX-XXXXXXX"
              />
            </div>
            <div className="col-span-2">
              <Label>Nome/Cargo (Para Assinatura) *</Label>
              <Input 
                name="contratante_nome_cargo" 
                value={settings.contratante_nome_cargo} 
                onChange={(e) => onChange(empresa, e)}
                placeholder="John Doe, CEO"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Assinatura Digital */}
      <Card className="bg-blue-50">
        <CardContent className="pt-4 space-y-3">
          <h3 className="font-bold text-lg">✍️ Assinatura da CONTRATANTE</h3>
          <p className="text-sm text-gray-600">
            Esta assinatura será usada em TODOS os waivers criados desta empresa.
          </p>
          <div>
            <Label>Assine aqui *</Label>
            <div className="border-2 border-blue-300 rounded bg-white">
              <SignatureCanvas
                ref={signRef}
                onEnd={() => handleSignatureEnd(empresa)}
                canvasProps={{
                  width: 600,
                  height: 150,
                  className: 'signature-canvas w-full'
                }}
              />
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => signRef.current?.clear()}
              className="mt-2"
            >
              🗑️ Limpar Assinatura
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            ⚙️ Configurações das Empresas - Dados da CONTRATANTE
          </DialogTitle>
          <p className="text-sm text-gray-600">
            Configure os dados de cada empresa. Estes dados serão usados automaticamente nos waivers.
          </p>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="pm_home">🏠 PM Home Remodeling</TabsTrigger>
            <TabsTrigger value="bimcle">🏗️ Bimcle Construction</TabsTrigger>
          </TabsList>
          
          <TabsContent value="pm_home" className="space-y-4">
            {renderForm(pmHomeSettings, handleChange, pmHomeSignRef, 'pm_home')}
          </TabsContent>
          
          <TabsContent value="bimcle" className="space-y-4">
            {renderForm(bimcleSettings, handleChange, bimcleSignRef, 'bimcle')}
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700">
            💾 Salvar Configurações de Ambas Empresas
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WaiverSettingsModal;
